/**
 * @file MilkPowderCostModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-04
 * @modified 2026-09-30
 * @description Modal for setting a single week's cost price against a milk
 *              powder product (sales account 200P). Keyed on the product +
 *              week-commencing — re-saving the same week overwrites it.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import Input from './Input';
import DateInput from './DateInput';
import { formatCurrency } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import ItemAccountsPanel from './ItemAccountsPanel';

// Monday of the current week as YYYY-MM-DD (local). Milk powder costs are
// quoted week-commencing Monday.
function MilkPowderCostModal({ product, prefill, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  useSlideOutButton([loading]);

  const [formData, setFormData] = useState({
    // Editing an existing row keeps its date; a new one starts empty. See the
    // note above currentWeekCommencing.
    week_commencing: prefill?.week_commencing
      ? String(prefill.week_commencing).slice(0, 10)
      : '',
    cost: prefill?.cost != null ? prefill.cost : '',
    notes: prefill?.notes || ''
  });

  useEffect(() => {
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) contentWrapper.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.week_commencing) {
      setError('Please select a week-commencing date');
      return;
    }
    if (formData.cost === '' || isNaN(parseFloat(formData.cost))) {
      setError('Please enter a valid cost');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          inventory_item_id: product.inventory_item_id,
          week_commencing: formData.week_commencing,
          cost: parseFloat(formData.cost),
          notes: formData.notes || null
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save cost price');
      }
      onSave();
    } catch (err) {
      console.error('Error saving milk powder cost price:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content weekly-cost-modal modal-md ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4 className="modal-title">Set New Weekly Item Cost</h4>
          <button className="btn-icon close-btn" onClick={handleClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
            {error && (
              <div className="error-message">
                <span className="material-symbols-rounded">error</span>
                {error}
              </div>
            )}

            <div className="info-panel">
              <span style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center', fontWeight: '600', fontSize: '12px' }}><span className="product-code">{product.item_code || '—'}</span>{product.item_name || '—'}</span>
              {product.current_cost != null && (
                <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Current Cost:</span>
                  <span style={{ fontWeight: '600', fontSize: '12px' }}>{formatCurrency(product.current_cost)}</span>
                </div>
              )}
            </div>

            <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginBottom: "var(--spacing-md)", gap: "var(--spacing-md)" }}>
              <div className="form-group">
                <DateInput
                  label="Week Commencing *"
                  value={formData.week_commencing}
                  onChange={(e) => handleChange('week_commencing', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Cost (£) <span className="required">*</span></label>
                <Input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => handleChange('cost', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <Input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Optional notes (e.g. supplier, price ref)"
                />
              </div>
            </div>

            {/* How the product posts to the ledger — read-only, from the item. */}
            <ItemAccountsPanel itemId={product.inventory_item_id} />
          </div>

          <div className="modal-footer" style={{ position: "absolute", bottom: "0", left: "0", right: "0" }}>
            <button type="submit" className="btn-slideout btn-slideout-primary" disabled={loading}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                  {loading ? 'sync' : 'save'}
                </span>
              </div>
              <span className="btn-label">{loading ? 'Saving...' : 'Save Cost'}</span>
            </button>
            <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose} disabled={loading}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">close</span>
              </div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default MilkPowderCostModal;
